/**
 * featurebase/service.ts
 * Singleton service for Featurebase SDK orchestration and auth token retrieval.
 *
 * Provides:
 * - Auth token (JWT) retrieval via edge function
 * - SDK command orchestration (boot, identify, shutdown, setTheme, openMessenger,
 *   closeMessenger, initEmbedWidget, initChangelogWidget, openChangelogPopup,
 *   markAllChangelogsViewed, initSurveyWidget)
 * - Client-side telemetry (no PII)
 *
 * Teardown boundary:
 * - Service owns: shutdown()
 * - Provider owns: consent storage, UI state, reload fallback
 */

import { supabase as supabaseClient } from '@/lib/supabase';

import { FEATUREBASE_APP_ID, FEATUREBASE_EDGE_FUNCTION, FEATUREBASE_ORGANIZATION } from './constants';
import type {
  FeaturebaseBootOptions,
  FeaturebaseChangelogCallback,
  FeaturebaseChangelogOptions,
  FeaturebaseEmbedCallback,
  FeaturebaseEmbedOptions,
  FeaturebaseFeedbackCallback,
  FeaturebaseFeedbackWidgetOptions,
  FeaturebaseIdentifyCallback,
  FeaturebaseIdentifyOptions,
  FeaturebaseResult,
  FeaturebaseSurveyCallback,
  FeaturebaseSurveyOptions,
  FeaturebaseTheme,
  JwtResponse,
} from './types/sdk';

// ===== FEATUREBASE SERVICE =====

/**
 * Featurebase Service
 *
 * Singleton service for all Featurebase SDK operations.
 *
 * Features:
 * - Auth token (JWT) retrieval from edge function (no client-side caching — JWTs expire)
 * - SDK command orchestration with guards for unloaded SDK
 * - Client-side telemetry (no PII — never logs email, JWT, userId, or name)
 * - All SDK commands wrapped in try/catch — never throws to callers
 */
class FeaturebaseService {
  // ===== PRIVATE STATE =====

  /** Supabase client instance */
  private supabase = supabaseClient;

  // ===== PRIVATE HELPERS: TELEMETRY =====

  /**
   * Emit client-side telemetry event
   *
   * Wrapped in try-catch to never break main flow.
   * Never logs PII (email, JWT, userId, name).
   *
   * @param payload - Telemetry data (event names, error codes, boolean flags only)
   */
  private emitTelemetry(payload: Record<string, unknown>): void {
    try {
      console.log(
        '📊 [FEATUREBASE]',
        JSON.stringify({
          ...payload,
          timestamp: new Date().toISOString(),
        })
      );
    } catch (err) {
      // Telemetry errors should never break functionality
      console.warn('⚠️ Featurebase telemetry emission failed:', err);
    }
  }

  // ===== PRIVATE HELPERS: RESPONSE VALIDATION =====

  /**
   * Runtime type guard for JWT edge function response
   *
   * @param data - Unknown response data from edge function
   * @returns True if data matches JwtResponse shape
   */
  private isValidJwtResponse(data: unknown): data is JwtResponse {
    return (
      typeof data === 'object' &&
      data !== null &&
      'jwt' in data &&
      typeof (data as JwtResponse).jwt === 'string'
    );
  }

  // ===== PUBLIC METHODS: AUTH TOKEN RETRIEVAL =====

  /**
   * Retrieve a signed JWT for Featurebase identity verification
   *
   * Invokes the featurebase-hmac edge function with optional user fields.
   * The edge function derives userId and email from the authenticated
   * Supabase session; optional fields (name, plan, createdAt) are
   * passed in the request body and embedded in the JWT.
   *
   * No client-side caching — JWTs expire (15 min). The edge function
   * is only called once per boot sequence (not per render).
   *
   * @param params - User fields to include in the JWT
   * @returns Success with JWT string, or error result
   */
  async getAuthToken(params: {
    name: string;
    plan?: string;
    createdAt?: string;
  }): Promise<FeaturebaseResult<string>> {
    try {
      this.emitTelemetry({ event: 'auth_token_fetch_start' });

      // Invoke edge function with user fields in body
      const { data, error } = await this.supabase.functions.invoke(
        FEATUREBASE_EDGE_FUNCTION,
        { body: params },
      );

      // Handle invocation errors (HTTP errors, network errors)
      if (error) {
        this.emitTelemetry({
          event: 'auth_token_fetch_error',
          error_code: 'AUTH_TOKEN_FETCH_FAILED',
        });

        return {
          success: false,
          error: {
            error_code: 'AUTH_TOKEN_FETCH_FAILED',
            message: error.message ?? 'Failed to fetch auth token',
          },
        };
      }

      // Validate response shape
      if (!this.isValidJwtResponse(data)) {
        this.emitTelemetry({
          event: 'auth_token_fetch_error',
          error_code: 'INVALID_RESPONSE',
        });

        return {
          success: false,
          error: {
            error_code: 'INVALID_RESPONSE',
            message: 'Malformed auth token response from server',
          },
        };
      }

      this.emitTelemetry({ event: 'auth_token_fetch_success' });

      return { success: true, data: data.jwt };
    } catch (err) {
      this.emitTelemetry({
        event: 'auth_token_fetch_error',
        error_code: 'AUTH_TOKEN_FETCH_FAILED',
      });

      return {
        success: false,
        error: {
          error_code: 'AUTH_TOKEN_FETCH_FAILED',
          message:
            err instanceof Error ? err.message : 'Failed to fetch auth token',
        },
      };
    }
  }

  // ===== PUBLIC METHODS: SDK COMMAND ORCHESTRATION =====

  /**
   * Initialize the Featurebase widget with a signed JWT
   *
   * Calls `window.Featurebase('boot', ...)` with the organization ID,
   * featurebaseJwt, theme, and hideDefaultLauncher: true.
   *
   * @param params - JWT and display options (appId and hideDefaultLauncher filled by service)
   */
  boot(
    params: Omit<FeaturebaseBootOptions, 'appId' | 'hideDefaultLauncher'>
  ): boolean {
    if (!FEATUREBASE_APP_ID) {
      this.emitTelemetry({ event: 'boot_skipped', reason: 'missing_app_id' });
      return false;
    }

    if (!window.Featurebase) {
      this.emitTelemetry({ event: 'boot_skipped', reason: 'sdk_not_loaded' });
      return false;
    }

    try {
      window.Featurebase('boot', {
        appId: FEATUREBASE_APP_ID,
        ...params,
        hideDefaultLauncher: true,
      });
      this.emitTelemetry({ event: 'boot_called' });
      return true;
    } catch {
      this.emitTelemetry({ event: 'boot_error' });
      return false;
    }
  }

  /**
   * Identify the user for all widgets (survey, changelog, portal)
   *
   * Calls `window.Featurebase('identify', ...)` with the organization ID
   * and signed JWT. Must be called after `boot()` to authenticate the user
   * for widgets beyond the Messenger.
   *
   * @param params - Identify options (organization filled by service)
   * @param callback - SDK callback (err is null on success)
   * @returns true if SDK call dispatched, false if guards failed
   */
  identify(
    params: Omit<FeaturebaseIdentifyOptions, 'organization'>,
    callback: FeaturebaseIdentifyCallback,
  ): boolean {
    if (!FEATUREBASE_ORGANIZATION) {
      this.emitTelemetry({ event: 'identify_skipped', reason: 'missing_organization' });
      return false;
    }

    if (!window.Featurebase) {
      this.emitTelemetry({ event: 'identify_skipped', reason: 'sdk_not_loaded' });
      return false;
    }

    try {
      window.Featurebase('identify', {
        organization: FEATUREBASE_ORGANIZATION,
        ...params,
      }, callback);
      this.emitTelemetry({ event: 'identify_called' });
      return true;
    } catch {
      this.emitTelemetry({ event: 'identify_error' });
      return false;
    }
  }

  /**
   * Initialize the Featurebase feedback widget
   *
   * Calls `window.Featurebase('initialize_feedback_widget', ...)` with
   * the organization ID and caller-supplied options. The `callback` is
   * invoked by the SDK on lifecycle events (widgetReady, feedbackSubmitted).
   *
   * Service does not own readiness state — the caller (Provider) sets
   * `feedbackReady` based on the return value of this method. The callback
   * is retained for telemetry (errors, feedbackSubmitted) but is not used
   * as the readiness gate since the SDK's `widgetReady` event is unreliable.
   *
   * @param params - Widget options (organization filled by service)
   * @param callback - SDK lifecycle callback (Provider-owned)
   * @returns true if SDK call dispatched, false if guards failed
   */
  initFeedbackWidget(
    params: Omit<FeaturebaseFeedbackWidgetOptions, 'organization'>,
    callback: FeaturebaseFeedbackCallback,
  ): boolean {
    if (!FEATUREBASE_ORGANIZATION) {
      this.emitTelemetry({ event: 'init_feedback_widget_skipped', reason: 'missing_organization' });
      return false;
    }

    if (!window.Featurebase) {
      this.emitTelemetry({ event: 'init_feedback_widget_skipped', reason: 'sdk_not_loaded' });
      return false;
    }

    try {
      window.Featurebase('initialize_feedback_widget', {
        organization: FEATUREBASE_ORGANIZATION,
        ...params,
      }, callback);
      this.emitTelemetry({ event: 'init_feedback_widget_called' });
      return true;
    } catch {
      this.emitTelemetry({ event: 'init_feedback_widget_error' });
      return false;
    }
  }

  /**
   * Shut down the Featurebase widget
   *
   * Calls `window.Featurebase('shutdown')` if the SDK is loaded.
   */
  shutdown(): void {
    if (!window.Featurebase) {
      this.emitTelemetry({ event: 'shutdown_called', sdk_loaded: false });
      return;
    }

    try {
      window.Featurebase('shutdown');
      this.emitTelemetry({ event: 'shutdown_called', sdk_loaded: true });
    } catch {
      this.emitTelemetry({ event: 'shutdown_error' });
    }
  }

  /**
   * Update the widget color theme
   *
   * Calls `window.Featurebase('setTheme', theme)` for dynamic
   * light/dark theme changes without re-booting the widget.
   *
   * @param theme - 'light' or 'dark'
   */
  setTheme(theme: FeaturebaseTheme): void {
    if (!window.Featurebase) {
      this.emitTelemetry({ event: 'set_theme_skipped' });
      return;
    }

    try {
      window.Featurebase('setTheme', theme);
      this.emitTelemetry({ event: 'set_theme_called' });
    } catch {
      this.emitTelemetry({ event: 'set_theme_error' });
    }
  }

  /**
   * Open the Featurebase Messenger widget
   *
   * Calls `window.Featurebase('show')` to display the Messenger
   * in its default view. No-op if the SDK is not loaded.
   */
  openMessenger(): void {
    if (!window.Featurebase) {
      this.emitTelemetry({ event: 'open_messenger_skipped', reason: 'sdk_not_loaded' });
      return;
    }

    try {
      window.Featurebase('show');
      this.emitTelemetry({ event: 'open_messenger_called' });
    } catch {
      this.emitTelemetry({ event: 'open_messenger_error' });
    }
  }

  /**
   * Open the Featurebase feedback widget
   *
   * Dispatches a `postMessage` to the Featurebase SDK iframe to open
   * the feedback widget. The caller must pass the current `feedbackReady`
   * state — the service does not own readiness.
   *
   * @param feedbackReady - Current feedback widget readiness (caller-owned)
   * @returns true if postMessage dispatched, false if not ready or dispatch failed
   */
  openFeedbackWidget(feedbackReady: boolean): boolean {
    if (!feedbackReady) {
      this.emitTelemetry({ event: 'open_feedback_widget_skipped', reason: 'not_ready' });
      return false;
    }

    try {
      window.postMessage({
        target: 'FeaturebaseWidget',
        data: { action: 'openFeedbackWidget' },
      }, '*');
      this.emitTelemetry({ event: 'open_feedback_widget_called' });
      return true;
    } catch {
      this.emitTelemetry({ event: 'open_feedback_widget_error' });
      return false;
    }
  }

  /**
   * Close the Featurebase Messenger widget
   *
   * Calls `window.Featurebase('hide')` to dismiss the Messenger.
   * No-op if the SDK is not loaded.
   */
  closeMessenger(): void {
    if (!window.Featurebase) {
      this.emitTelemetry({ event: 'close_messenger_skipped', reason: 'sdk_not_loaded' });
      return;
    }

    try {
      window.Featurebase('hide');
      this.emitTelemetry({ event: 'close_messenger_called' });
    } catch {
      this.emitTelemetry({ event: 'close_messenger_error' });
    }
  }

  // ===== PUBLIC METHODS: EMBED WIDGET =====

  /**
   * Initialize the Featurebase embedded portal widget
   *
   * Calls `window.Featurebase('init_embed_widget', ...)` with the
   * organization ID and caller-supplied options. Renders the portal
   * inside a `<div data-featurebase-embed>` container.
   *
   * @param params - Widget options (organization filled by service)
   * @param callback - SDK lifecycle callback (called on widgetReady)
   * @returns true if SDK call dispatched, false if guards failed
   */
  initEmbedWidget(
    params: Omit<FeaturebaseEmbedOptions, 'organization'>,
    callback: FeaturebaseEmbedCallback,
  ): boolean {
    if (!FEATUREBASE_ORGANIZATION) {
      this.emitTelemetry({ event: 'init_embed_widget_skipped', reason: 'missing_organization' });
      return false;
    }

    if (!window.Featurebase) {
      this.emitTelemetry({ event: 'init_embed_widget_skipped', reason: 'sdk_not_loaded' });
      return false;
    }

    try {
      window.Featurebase('init_embed_widget', {
        organization: FEATUREBASE_ORGANIZATION,
        ...params,
      }, callback);
      this.emitTelemetry({ event: 'init_embed_widget_called' });
      return true;
    } catch {
      this.emitTelemetry({ event: 'init_embed_widget_error' });
      return false;
    }
  }

  // ===== PUBLIC METHODS: CHANGELOG WIDGET =====

  /**
   * Initialize the Featurebase changelog widget
   *
   * Calls `window.Featurebase('init_changelog_widget', ...)` with the
   * organization ID and caller-supplied options. The callback is invoked
   * by the SDK when the unread changelog count changes.
   *
   * @param params - Widget options (organization filled by service)
   * @param callback - SDK callback for unread count changes
   * @returns true if SDK call dispatched, false if guards failed
   */
  initChangelogWidget(
    params: Omit<FeaturebaseChangelogOptions, 'organization'>,
    callback: FeaturebaseChangelogCallback,
  ): boolean {
    if (!FEATUREBASE_ORGANIZATION) {
      this.emitTelemetry({ event: 'init_changelog_widget_skipped', reason: 'missing_organization' });
      return false;
    }

    if (!window.Featurebase) {
      this.emitTelemetry({ event: 'init_changelog_widget_skipped', reason: 'sdk_not_loaded' });
      return false;
    }

    try {
      window.Featurebase('init_changelog_widget', {
        organization: FEATUREBASE_ORGANIZATION,
        ...params,
      }, callback);
      this.emitTelemetry({ event: 'init_changelog_widget_called' });
      return true;
    } catch {
      this.emitTelemetry({ event: 'init_changelog_widget_error' });
      return false;
    }
  }

  /**
   * Open the changelog popup
   *
   * Calls `window.Featurebase('manually_open_changelog_popup')` to display
   * the changelog popup. Optionally accepts a slug to open a specific update.
   * No-op if the SDK is not loaded.
   *
   * @param slug - Optional changelog entry slug to open a specific update
   */
  openChangelogPopup(slug?: string): void {
    if (!window.Featurebase) {
      this.emitTelemetry({ event: 'open_changelog_popup_skipped', reason: 'sdk_not_loaded' });
      return;
    }

    try {
      if (slug) {
        window.Featurebase('manually_open_changelog_popup', { slug });
      } else {
        window.Featurebase('manually_open_changelog_popup');
      }
      this.emitTelemetry({ event: 'open_changelog_popup_called' });
    } catch {
      this.emitTelemetry({ event: 'open_changelog_popup_error' });
    }
  }

  /**
   * Mark all changelog entries as viewed
   *
   * Calls `window.Featurebase('set_all_changelogs_as_viewed')` to reset
   * the unread count. No-op if the SDK is not loaded.
   */
  markAllChangelogsViewed(): void {
    if (!window.Featurebase) {
      this.emitTelemetry({ event: 'mark_all_changelogs_viewed_skipped', reason: 'sdk_not_loaded' });
      return;
    }

    try {
      window.Featurebase('set_all_changelogs_as_viewed');
      this.emitTelemetry({ event: 'mark_all_changelogs_viewed_called' });
    } catch {
      this.emitTelemetry({ event: 'mark_all_changelogs_viewed_error' });
    }
  }

  // ===== PUBLIC METHODS: SURVEY WIDGET =====

  /**
   * Initialize the Featurebase survey widget
   *
   * Calls `window.Featurebase('initialize_survey_widget', ...)` with the
   * organization ID and caller-supplied options. Once initialized, surveys
   * trigger automatically based on Featurebase dashboard targeting rules.
   *
   * @param params - Widget options (organization filled by service)
   * @param callback - SDK callback for initialization success/failure
   * @returns true if SDK call dispatched, false if guards failed
   */
  initSurveyWidget(
    params: Omit<FeaturebaseSurveyOptions, 'organization'>,
    callback: FeaturebaseSurveyCallback,
  ): boolean {
    if (!FEATUREBASE_ORGANIZATION) {
      this.emitTelemetry({ event: 'init_survey_widget_skipped', reason: 'missing_organization' });
      return false;
    }

    if (!window.Featurebase) {
      this.emitTelemetry({ event: 'init_survey_widget_skipped', reason: 'sdk_not_loaded' });
      return false;
    }

    if (!params.featurebaseJwt) {
      this.emitTelemetry({ event: 'init_survey_widget_skipped', reason: 'missing_jwt' });
      return false;
    }

    try {
      window.Featurebase('initialize_survey_widget', {
        organization: FEATUREBASE_ORGANIZATION,
        theme: params.theme,
        placement: params.placement,
        featurebaseJwt: params.featurebaseJwt,
        locale: params.locale,
      }, callback);
      this.emitTelemetry({ event: 'init_survey_widget_called' });
      return true;
    } catch {
      this.emitTelemetry({ event: 'init_survey_widget_error' });
      return false;
    }
  }
}

// ===== SINGLETON EXPORT =====

/**
 * Featurebase Service singleton
 *
 * Import this to access all Featurebase operations.
 *
 * @example
 * import { featurebaseService } from '@/featurebase';
 *
 * const result = await featurebaseService.getAuthToken({ name: 'User' });
 * if (result.success) {
 *   featurebaseService.boot({ featurebaseJwt: result.data, theme: 'dark', language: 'en' });
 * }
 */
export const featurebaseService = new FeaturebaseService();
